// Audit log (docs/cockpit/spec.md, ADR-0012): everything done in the business, by whom (a person, or LAMP and the
// agent that acted), oldest first, filtered by Run, Business Object, person, agent and dates, a page at a time.
// Export downloads every entry matching the filters as CSV. The Audit Log is append-only; nothing is changed here.
// Each row is in words (#462): who by name, what as a sentence, the Run by what it is about. Ids only in the CSV.
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { ApiError, type Api, type AuditEntry, type AuditFilters } from "../api.js";
import { locale } from "../i18n/format.js";
import type { Language } from "../i18n/language.js";
import { CATALOGUES, useLanguage, useWords, type Words } from "../i18n/words.js";
import { aboutOf, objectName, whatOf, whoOf } from "../runs/panel.js";

/** Entries a page shows at once. */
export const PAGE_SIZE = 50;
/** Entries an export reads at once: the most `GET /audit` answers. */
const EXPORT_PAGE = 200;

const at = (iso: string, language: Language) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : new Intl.DateTimeFormat(locale(language), { dateStyle: "medium", timeStyle: "short" }).format(date);
};

const problemOf = (error: unknown, w: Words) =>
  error instanceof ApiError
    ? error.problems.length > 0
      ? error.problems.join("; ")
      : w.ownerPagesRefused(error.code)
    : w.ownerPagesUnreachable;

/** The form's fields; dates are whole days in this device's time zone. */
export interface AuditForm {
  readonly run: string;
  readonly object: string;
  readonly actor: string;
  readonly agent: string;
  readonly from: string;
  readonly to: string;
}
const EMPTY: AuditForm = { run: "", object: "", actor: "", agent: "", from: "", to: "" };

const startOf = (day: string) => new Date(`${day}T00:00:00`);

/** The form as `GET /audit` filters: from the start of `from` up to the end of `to`. */
export function filtersOf(form: AuditForm): AuditFilters {
  const filters: { -readonly [K in keyof AuditFilters]: string } = {};
  for (const name of ["run", "object", "actor", "agent"] as const) {
    const value = form[name].trim();
    if (value) filters[name] = value;
  }
  if (form.from) filters.from = startOf(form.from).toISOString();
  if (form.to) {
    const end = startOf(form.to);
    end.setDate(end.getDate() + 1);
    filters.to = end.toISOString();
  }
  return filters;
}

/** The Business Object name of an entry about the Tenant itself (the api's `TENANT_OBJECT`), such as its Supplier Particulars. */
const TENANT_OBJECT = "tenant";

/**
 * The Business Object as the page shows it: by its kind and number, never its id; the Tenant is "the business" (#343).
 */
const objectShown = (e: AuditEntry, words: Words) => {
  if (!e.object) return "";
  if (e.object.name === TENANT_OBJECT) return words.auditTheBusiness;
  const number = e.detail["number"];
  return typeof number === "string" && number !== ""
    ? `${objectName(e.object.name, words)} ${number}`
    : objectName(e.object.name, words);
};

/** A CSV cell: quoted when it must be, and never read as a spreadsheet formula. */
function cell(value: string): string {
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safe) ? `"${safe.replaceAll('"', '""')}"` : safe;
}

/** The entries as CSV, one row each, oldest first, headed in the person's words: the rows as shown, then their ids. */
export function auditCsv(entries: readonly AuditEntry[], words: Words = CATALOGUES.en): string {
  const head = words.auditCsvColumns;
  const rows = entries.map((e) => [
    String(e.sequence),
    e.occurredAt,
    whatOf(e, words),
    whoOf(e, words),
    e.run ? (aboutOf(e, words) ?? words.auditTheRun) : "",
    e.names?.step ?? "",
    objectShown(e, words),
    e.action,
    e.actor.kind === "person" ? e.actor.id : "",
    e.agent ?? "",
    e.run ?? "",
    e.node ?? "",
    e.object?.id ?? "",
  ]);
  return [head, ...rows].map((r) => r.map(cell).join(",")).join("\r\n") + "\r\n";
}

function downloadFile(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

/** The Run an entry is on, by what it is about, opening its panel. */
function RunButton({ entry, run, onOpenRun }: { entry: AuditEntry; run: string; onOpenRun: (run: string) => void }) {
  const w = useWords();
  return (
    <button type="button" className="btn btn-small" onClick={() => onOpenRun(run)}>
      {aboutOf(entry, w) ?? w.auditOpenRun}
    </button>
  );
}

type FieldLabel = "auditRun" | "auditBusinessObject" | "auditPerson" | "auditAgent" | "auditFrom" | "auditTo";
const FIELDS: readonly { readonly name: keyof AuditForm; readonly label: FieldLabel; readonly type?: string }[] = [
  { name: "run", label: "auditRun" },
  { name: "object", label: "auditBusinessObject" },
  { name: "actor", label: "auditPerson" },
  { name: "agent", label: "auditAgent" },
  { name: "from", label: "auditFrom", type: "date" },
  { name: "to", label: "auditTo", type: "date" },
];

export function AuditLog({
  api,
  onOpenRun,
  download = downloadFile,
}: {
  api: Api;
  onOpenRun: (run: string) => void;
  download?: (name: string, text: string) => void;
}) {
  const w = useWords();
  const { language } = useLanguage();
  const [form, setForm] = useState<AuditForm>(EMPTY);
  const [filters, setFilters] = useState<AuditFilters>({});
  const [entries, setEntries] = useState<readonly AuditEntry[] | undefined>(undefined);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  // Only the latest request's answer is shown: an earlier one answered later never replaces it.
  const latest = useRef(0);
  // The filters in force, for a load the page starts again on its own (its words or API changed).
  const applied = useRef<AuditFilters>({});

  const load = useCallback(
    async (shown: AuditFilters, before: readonly AuditEntry[]) => {
      const request = ++latest.current;
      setBusy(true);
      setProblem(undefined);
      try {
        const last = before.at(-1);
        const page = await api.auditPage(last ? { ...shown, after: String(last.sequence) } : shown, PAGE_SIZE);
        if (request !== latest.current) return;
        setEntries([...before, ...page]);
        setMore(page.length === PAGE_SIZE);
      } catch (error) {
        if (request === latest.current) setProblem(problemOf(error, w));
      } finally {
        if (request === latest.current) setBusy(false);
      }
    },
    [api, w],
  );

  useEffect(() => {
    void load(applied.current, []);
  }, [load]);

  const filter = (event: FormEvent) => {
    event.preventDefault();
    const next = filtersOf(form);
    applied.current = next;
    setFilters(next);
    void load(next, []);
  };

  const exportAll = async () => {
    setBusy(true);
    setProblem(undefined);
    try {
      const all: AuditEntry[] = [];
      for (;;) {
        const last = all.at(-1);
        const page = await api.auditPage(last ? { ...filters, after: String(last.sequence) } : filters, EXPORT_PAGE);
        all.push(...page);
        if (page.length < EXPORT_PAGE) break;
      }
      download("audit-log.csv", auditCsv(all, w));
    } catch (error) {
      setProblem(problemOf(error, w));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <p className="muted">{w.auditIntro}</p>
      <form className="audit-filters" aria-label={w.auditFilterLabel} onSubmit={filter}>
        {FIELDS.map((f) => (
          <label key={f.name} className="field">
            {w[f.label]}{" "}
            <input
              type={f.type ?? "text"}
              value={form[f.name]}
              onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
            />
          </label>
        ))}
        <button type="submit" className="btn" disabled={busy}>
          {w.auditFilter}
        </button>
        <button type="button" className="btn" disabled={busy} onClick={() => void exportAll()}>
          {w.auditExport}
        </button>
      </form>
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      {entries === undefined ? (
        problem ? null : (
          <p role="status">{w.auditLoading}</p>
        )
      ) : entries.length === 0 ? (
        <p className="muted">{w.auditNoEntries}</p>
      ) : (
        <>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th scope="col">{w.auditWhen}</th>
                  <th scope="col">{w.auditWhat}</th>
                  <th scope="col">{w.auditWho}</th>
                  <th scope="col">{w.auditRun}</th>
                  <th scope="col">{w.auditBusinessObject}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.sequence}>
                    <td>
                      <time dateTime={e.occurredAt}>{at(e.occurredAt, language)}</time>
                    </td>
                    <td>{whatOf(e, w)}</td>
                    <td>{whoOf(e, w)}</td>
                    <td>{e.run ? <RunButton entry={e} run={e.run} onOpenRun={onOpenRun} /> : null}</td>
                    <td>{objectShown(e, w)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {more ? (
            <p>
              <button type="button" className="btn" disabled={busy} onClick={() => void load(filters, entries)}>
                {w.auditShowMore}
              </button>
            </p>
          ) : null}
        </>
      )}
    </>
  );
}
